/**
 * Player-facing failure text, in ONE place.
 *
 * The sim emits these strings in `invalid` events, the client displays them,
 * and the client's routing cancel compares one of them BY IDENTITY (never by
 * substring — a reword used to silently break the pending-interact cancel).
 * Scenario fixtures assert the exact string, since a rejected command changes
 * no state: the event IS the observable.
 *
 * House style for world-facing copy (docs/game_mechanics.md): sentence case,
 * one sentence, terminal period, no em dashes, at most 8 words. Name the
 * thing the player failed against, not the internal id.
 */
export const REASON = {
  noPuzzleTile: "There is no puzzle tile there.",
  puzzleUnsolved: "Solve the puzzle first.",
  puzzleAlreadySolved: "The puzzle is already solved.",
  // --- the floor itself -------------------------------------------------
  notInFloor: "You are not in this floor.",
  leftFloor: "You have left this floor.",
  floorEnded: "This floor has ended.",
  dead: "You are dead.",

  // --- movement ---------------------------------------------------------
  iceSliding: "You are still sliding.",
  walkBlocked: "You can't walk there.",
  stepCloser: "Step closer.",

  // --- attacking --------------------------------------------------------
  nothingToAttack: "There's nothing to attack there.",
  cantReach: "You can't reach that.",
  outOfRange: "That's out of range.",
  alreadySwinging: "You are already swinging.",
  targetGone: "That target is gone.",
  noAttacker: "You can't attack.",

  // --- doors, keys, the boss -------------------------------------------
  noDoor: "There's no door there.",
  guardiansAlive: "Guardians still hold that room.",
  needKey: "You need the matching key.",
  noSuchKey: "There's no such key.",
  notCarryingKey: "You don't carry that key.",
  bossAbsent: "There's no boss here.",
  bossAlive: "The boss is still alive.",

  // --- portals ----------------------------------------------------------
  noReturnPortal: "There's no return portal here.",
  nothingToReturnTo: "There's nothing to return to yet.",

  // --- gatestones -------------------------------------------------------
  noStone: "There's no such gatestone.",
  stoneCarried: "That gatestone is carried.",
  notCarryingStone: "You don't carry that gatestone.",
  noGroupStone: "There's no group gatestone.",
  noPersonalStone: "Summon a personal gatestone first.",

  // --- items, pockets, sacks -------------------------------------------
  nothingHere: "There's nothing here to pick up.",
  noSack: "There's no sack here.",
  sackEmpty: "The sack has nothing like that.",
  pocketFull: "Your pocket is full.",
  notCarrying: "You don't carry that.",
  notCarryingItem: "You don't carry that item.",
  alreadyWearing: "You already wear that.",
  noEquipmentSlot: "There's no such equipment slot.",
  emptySlot: "That slot is empty.",
  badReorder: "That order doesn't match what you carry.",
  notConsumable: "That's not a consumable.",
  noConsumable: "There's no such consumable.",

  // --- abilities --------------------------------------------------------
  unknownAbility: "You don't know that ability.",
  unknownLevel: "That level isn't available.",
  abilityUnavailable: "That ability isn't available yet.",
  chargeHeld: "Your next swing is already charged.",
  offMap: "That's off the map.",
  notVisible: "You can't see there.",
  cantSeeTile: "You can't see that tile.",
  wellNeedsFloor: "The well needs a floor tile.",
  cantTeleport: "You can't teleport there.",
  tileNotInRoom: "That tile isn't in this room.",
  alreadyThere: "You're already there.",
  nothingToGrab: "There's nothing to grab there.",
  itemNotInRoom: "That item isn't in this room.",

  // --- chat / unclassified ---------------------------------------------
  emptyMessage: "Type a message first.",
  opUnavailable: "That action isn't available.",
} as const;

/** An aimed ability that arrived with no tile. */
export const aimAtTile = (name: string): string => `Aim ${name} at a tile.`;
/** The ability's cooldown window still holds. */
export const onCooldown = (name: string): string => `${name} is not ready yet.`;
/** A store row is missing the duration the kind needs (a tuning error). */
export const noDurationSet = (name: string): string => `${name} has no duration set.`;
/** A store row is missing the radius the kind needs (a tuning error). */
export const noRadiusSet = (name: string): string => `${name} has no radius set.`;
/** The kit has no magic weapon, so the spell has nothing to cast through. */
export const needsMagicWeapon = (name: string): string => `${name} needs a magic weapon.`;
/** The cast needs a target item on the floor. */
export const needsItem = (name: string): string => `${name} needs an item.`;
